import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Sparkles, UserCircle, PlusCircle, Share2, LayoutDashboard, BookOpen } from 'lucide-react';
import FounderLink from './FounderLink';

type WelcomeGuideModalProps = {
  open: boolean;
  onClose: () => void;
  /** When true, show “Get started” links to signup/login */
  showAuthLinks?: boolean;
};

const WelcomeGuideModal: React.FC<WelcomeGuideModalProps> = ({ open, onClose, showAuthLinks }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-tech-border bg-tech-darker shadow-2xl shadow-tech-cyan/10"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-lg text-tech-text-muted hover:text-tech-text hover:bg-tech-gray transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 md:p-8 pt-10">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 text-tech-cyan flex-shrink-0" aria-hidden />
            <h2 id="welcome-title" className="text-2xl font-bold text-tech-text">
              Welcome to GNK Continuum
            </h2>
          </div>

          <p className="text-tech-text-muted leading-relaxed mb-6">
            GNK Continuum is a task and collaboration workspace: create tasks, build a shareable Tasker profile,
            follow implementation guides, and use the dashboard to stay organized. It pairs with the broader
            3rd Eye / GNK ecosystem for productivity and connection.
          </p>

          <h3 className="text-sm font-semibold uppercase tracking-wide text-tech-cyan mb-3">Quick start</h3>
          <ol className="space-y-3 text-sm text-tech-text-muted mb-6 list-decimal list-inside">
            <li className="pl-1">
              <span className="text-tech-text font-medium">Account — </span>
              Sign up with email or Google, then sign in anytime from the header.
            </li>
            <li className="pl-1 flex flex-wrap items-start gap-x-1">
              <UserCircle className="w-4 h-4 text-tech-cyan inline mt-0.5 flex-shrink-0" aria-hidden />
              <span>
                <span className="text-tech-text font-medium">Profile — </span>
                Open <strong className="text-tech-text">Profile</strong> and complete your Tasker profile so others
                can see your skills. Use <strong className="text-tech-text">Profile status</strong> on Home to track completion.
              </span>
            </li>
            <li className="pl-1 flex flex-wrap items-start gap-x-1">
              <PlusCircle className="w-4 h-4 text-tech-cyan inline mt-0.5 flex-shrink-0" aria-hidden />
              <span>
                <span className="text-tech-text font-medium">Tasks — </span>
                Use <strong className="text-tech-text">Create task</strong> (Task Hatch) to add work items, then{' '}
                <strong className="text-tech-text">Dashboard</strong> to manage them.
              </span>
            </li>
            <li className="pl-1 flex flex-wrap items-start gap-x-1">
              <Share2 className="w-4 h-4 text-tech-accent inline mt-0.5 flex-shrink-0" aria-hidden />
              <span>
                <span className="text-tech-text font-medium">Share — </span>
                Your public profile lives at a link you can share (from Profile / Tasker settings). Optional: verify
                your email in Settings for extra trust.
              </span>
            </li>
            <li className="pl-1 flex flex-wrap items-start gap-x-1">
              <BookOpen className="w-4 h-4 text-tech-cyan inline mt-0.5 flex-shrink-0" aria-hidden />
              <span>
                <span className="text-tech-text font-medium">Guides — </span>
                Browse <strong className="text-tech-text">Implementation guides</strong> and{' '}
                <strong className="text-tech-text">Search guides</strong> for how-tos.
              </span>
            </li>
          </ol>

          <div className="flex flex-wrap gap-2 mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-tech-gray/80 border border-tech-border text-xs text-tech-text-muted">
              <LayoutDashboard className="w-4 h-4 text-tech-cyan" aria-hidden />
              Tip: Use the top navigation after you sign in — everything important is one click away.
            </div>
          </div>

          {showAuthLinks && (
            <div className="flex flex-wrap gap-3 mb-6">
              <Link
                to="/signup"
                onClick={onClose}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl font-semibold text-tech-dark"
                style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
              >
                Create account
              </Link>
              <Link
                to="/login"
                onClick={onClose}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl font-semibold border border-tech-cyan text-tech-cyan hover:bg-tech-cyan/10 transition-colors"
              >
                I already have an account
              </Link>
            </div>
          )}

          <div className="border-t border-tech-border pt-5">
            <p className="text-xs text-tech-text-muted mb-3">Founder</p>
            <FounderLink variant="modal" onNavigate={onClose} />
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-6 w-full py-3 rounded-xl font-medium bg-tech-gray border border-tech-border text-tech-text hover:bg-tech-gray-light transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeGuideModal;
